import {
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";

export const generatePaginationLinks = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
): React.JSX.Element[] => {
  const pages: React.JSX.Element[] = [];
  const MAX_VISIBLE_PAGES = 5; // Adjust this number as needed
  const ELLIPSIS_THRESHOLD = 2;

  // Helper function to create page item
  const createPageItem = (page: number): React.JSX.Element => (
    <PaginationItem key={`page-${page}`}>
      <PaginationLink
        onClick={() => onPageChange(page)}
        isActive={page === currentPage}
      >
        {page}
      </PaginationLink>
    </PaginationItem>
  );

  // Simple case - show all pages
  if (totalPages <= MAX_VISIBLE_PAGES) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(createPageItem(i));
    }

    return pages;
  }

  // Always show first page
  pages.push(createPageItem(1));

  // Show left ellipsis if current page is far from start
  if (currentPage > ELLIPSIS_THRESHOLD + 2) {
    pages.push(
      <PaginationItem key='left-ellipsis'>
        <PaginationEllipsis />
      </PaginationItem>,
    );
  }

  // Calculate range of pages to show around current page
  let startPage = Math.max(2, currentPage - ELLIPSIS_THRESHOLD);
  let endPage = Math.min(totalPages - 1, currentPage + ELLIPSIS_THRESHOLD);

  // Adjust if we're near the boundaries
  if (currentPage <= ELLIPSIS_THRESHOLD + 1) {
    endPage = 1 + MAX_VISIBLE_PAGES - 2; // -2 for first and last pages
  } else if (currentPage >= totalPages - ELLIPSIS_THRESHOLD) {
    startPage = totalPages - (MAX_VISIBLE_PAGES - 2);
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i++) {
    pages.push(createPageItem(i));
  }

  // Show right ellipsis if current page is far from end
  if (currentPage < totalPages - (ELLIPSIS_THRESHOLD + 1)) {
    pages.push(
      <PaginationItem key='right-ellipsis'>
        <PaginationEllipsis />
      </PaginationItem>,
    );
  }

  // Always show last page
  pages.push(createPageItem(totalPages));

  return pages;
};
